import React, {useEffect, useState, useCallback} from 'react';
import axios from 'axios';
import TakeAttendance from './TakeAttendance';
import './attendance.css';
import {
    Container,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress
} from '@mui/material';

const AttendanceHome = () => {
    const [sectionPeriods, setSectionPeriods] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [canOverride, setCanOverride] = useState(false); // users who can select teacher (admins/office/dean)
    const [currentTeacherId, setCurrentTeacherId] = useState(null); // Current logged-in teacher ID
    const [isTeacher, setIsTeacher] = useState(false); // Whether current user is a teacher
    const [loading, setLoading] = useState(true); // Loading state for better UX
    const [message, setMessage] = useState(''); // State to store the backend message

    const fetchTeachers = useCallback(() => {
        setLoading(true);
        axios.get('/api/teachers/all/')
            .then(response => {
                console.log("Fetched Teachers Data:", response.data);
                setTeachers(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching teachers:', error);
                setTeachers([]); // Fallback to empty array
                setLoading(false);
            });
    }, []);

    const fetchSectionPeriods = useCallback((teacherId = null) => {
        setLoading(true);
        const url = teacherId
            ? `/attendance/section-periods/?date=${selectedDate}&teacher_id=${teacherId}`
            : `/attendance/section-periods/?date=${selectedDate}`;
        axios.get(url)
            .then(response => {
                console.log("Fetched Section Periods Data:", response.data);
                if (response.data.message) {
                    setMessage(response.data.message); // Set the message from the backend
                    setSectionPeriods([]); // Clear periods if no section periods are available
                    setLoading(false);
                    return;
                }
                setMessage(''); // Clear any previous message
                const periods = Array.isArray(response.data.section_periods) ? response.data.section_periods : [];
                // Filter SectionPeriods for the selected teacher if `teacherId` is provided
                const filteredPeriods = teacherId
                    ? periods.filter(sp => sp.teachers.some(t => t.id === parseInt(teacherId)))
                    : periods;
                console.log("Filtered Section Periods:", filteredPeriods);
                setSectionPeriods(filteredPeriods);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching section periods:', error);
                setSectionPeriods([]);
                setLoading(false);
            });
    }, [selectedDate]);

    // After memoizing fetchers, run effect to load data
    useEffect(() => {
        axios.get('/api/current-user/')
            .then(response => {
                // Prefer granular permission flag from backend
                const override = !!(response.data?.can_take_any || response.data?.is_superuser);
                const teacherId = response.data?.teacher_id;
                const isTeacherUser = response.data?.is_teacher;
                
                setCanOverride(override);
                setCurrentTeacherId(teacherId);
                setIsTeacher(isTeacherUser);

                // Always fetch teachers list so we can show the dropdown
                fetchTeachers();

                // Auto-select current teacher if they are a teacher
                if (teacherId) {
                    setSelectedTeacher(teacherId);
                    fetchSectionPeriods(teacherId);
                } else if (override) {
                    // For admins without teacher record, fetch all section periods
                    fetchSectionPeriods();
                } else {
                    // For regular users, fetch their section periods
                    fetchSectionPeriods();
                }
            })
            .catch(error => {
                console.error('Error fetching current user:', error);
                setLoading(false);
            });
    }, [selectedDate, fetchSectionPeriods, fetchTeachers]);

    const handleTeacherChange = (e) => {
        const teacherId = e.target.value;
        setSelectedTeacher(teacherId);
        setSelectedPeriod(null); // reset period when teacher changes to avoid stale mixed data
        fetchSectionPeriods(teacherId);
    };

    const handlePeriodChange = (e) => {
        const periodId = e.target.value;
        const period = sectionPeriods.find(sp => sp.id === parseInt(periodId));
        setSelectedPeriod(period || null); // Fallback to null if not found
        // If the period has a single teacher, auto-select that teacher for convenience
        if (period && Array.isArray(period.teachers) && period.teachers.length === 1) {
            setSelectedTeacher(period.teachers[0].id);
        }
    };

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        setSelectedPeriod(null); // Reset selected period when date changes
        // Keep teacher selection if it's the current teacher, only reset for admins
        if (canOverride && !currentTeacherId) {
            setSelectedTeacher(null);
        }
    };

    return (
        <Container maxWidth={false} className="attendance-container">
            <Typography variant="h4" component="h2" className="attendance-header">
                Attendance
            </Typography>

            <Box className="attendance-toolbar" sx={{display: 'flex', gap: 2, alignItems: 'end'}}>
                <Box className="date-picker-container">
                    <Typography variant="caption" component="label" htmlFor="attendance-date">Date</Typography>
                    <input
                        type="date"
                        id="attendance-date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="date-picker"
                    />
                </Box>

                {(canOverride || isTeacher) && (
                    <FormControl size="small" className="teacher-select" sx={{minWidth: 220}}>
                        <InputLabel id="teacher-label">Teacher</InputLabel>
                        <Select
                            labelId="teacher-label"
                            id="teacher"
                            label="Teacher"
                            value={selectedTeacher || ''}
                            onChange={handleTeacherChange}
                            disabled={isTeacher && !canOverride} // Disable for regular teachers (they can only see their own)
                        >
                            {canOverride && (
                                <MenuItem value="">
                                    <em>All Teachers</em>
                                </MenuItem>
                            )}
                            {teachers.map(teacher => (
                                <MenuItem key={teacher.id} value={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {message && (
                <Alert severity={message.includes('holiday') ? 'error' : 'info'} sx={{mb: 2}}>
                    {message}
                </Alert>
            )}

            {loading ? (
                <Box className="loading-container">
                    <CircularProgress size={28} />
                    <Typography variant="body2">Loading attendance data…</Typography>
                </Box>
            ) : (
                <Box className="section-period-select" sx={{mt: 1}}>
                    <FormControl size="small" fullWidth sx={{maxWidth: 420}}>
                        <InputLabel id="section-period-label">Section Period</InputLabel>
                        <Select
                            labelId="section-period-label"
                            id="sectionPeriod"
                            label="Section Period"
                            onChange={handlePeriodChange}
                            value={selectedPeriod ? selectedPeriod.id : ''}
                        >
                            <MenuItem value=""><em>Select a period</em></MenuItem>
                            {sectionPeriods.map(sp => (
                                <MenuItem key={sp.id} value={sp.id}>
                                    {sp.start_time} - {sp.end_time} | Teacher: {sp.teachers.map(t => t.last_name).join(', ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {sectionPeriods.length === 0 && (
                        <Typography variant="body2" sx={{mt: 1}}>No section periods available for the selected date.</Typography>
                    )}
                </Box>
            )}

            {!message.includes('holiday') && selectedPeriod ? (
                canOverride && !selectedTeacher ? (
                    <Typography variant="body2" className="helper-text">
                        Select a teacher to take attendance for this period.
                    </Typography>
                ) : (
                    <TakeAttendance
                        sectionPeriod={selectedPeriod}
                        closeAttendance={() => setSelectedPeriod(null)}
                        selectedDate={selectedDate}
                        teacherId={selectedTeacher || null}
                    />
                )
            ) : (
                <Typography variant="body2" className="helper-text">
                    {!message ? 'Select a section period to take attendance.' : ''}
                </Typography>
            )}

        </Container>
    );
};

export default AttendanceHome;
